import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { ownErrorToastOptions } from 'src/app/shared/interceptors/http-error.interceptor';
import { NewBadge } from 'src/app/shared/utils/new-badge';

/** Resposta da API: a lista em vigor, ordenada por chave. */
export interface NewBadgeList {
  data: NewBadge[];
}

/**
 * Quais itens do menu mostram a etiqueta "Novo", e até quando (CU-17tm466mg60).
 *
 * A lista é configuração global (`MENU_NEW_BADGES`), editada pelo admin. O menu
 * lê UMA vez por carregamento do painel: o admin liga ou desliga e o cliente vê
 * no próximo carregamento, sem uma requisição a cada navegação.
 *
 * Falha da API não quebra o menu: vira lista vazia (nenhum item com etiqueta), sem
 * toast — a etiqueta é enfeite, e um aviso de erro por causa dela assustaria mais
 * do que ajudaria. A próxima montagem do menu (novo login no mesmo SPA) tenta de novo.
 */
@Injectable({ providedIn: 'root' })
export class MenuNewBadgeService {

  private readonly badges$ = new ReplaySubject<NewBadge[]>(1);
  private requested = false;

  /** O GET do boot em voo: a lista do `reload()`/`save()` é mais nova e o cancela. */
  private bootRequest: Subscription | null = null;

  constructor(private http: HttpClient) {}

  /** Uma leitura só da resposta: o que não for lista vira lista vazia. */
  private static toList(response: NewBadgeList | null): NewBadge[] {
    return Array.isArray(response?.data) ? response.data : [];
  }

  /** A lista em vigor; a primeira chamada busca na API, as outras reaproveitam. */
  badges(): Observable<NewBadge[]> {
    if (!this.requested) {
      this.requested = true;
      this.bootRequest = this.fetch().subscribe({
        next: list => this.badges$.next(list),
        error: () => {
          this.requested = false;
          this.badges$.next([]);
        },
      });
    }

    return this.badges$.asObservable();
  }

  /**
   * A lista lida agora na API, para a tela de admin: ela não pode editar em cima
   * do que o menu leu no boot. Erros ficam com quem chamou.
   */
  reload(): Observable<NewBadge[]> {
    return this.fetch().pipe(tap(list => this.publish(list)));
  }

  /**
   * Substitui a lista inteira (só administrador). O menu de quem salvou já reflete
   * na hora; o dos clientes, no próximo carregamento. Erros ficam com a tela.
   */
  save(badges: NewBadge[]): Observable<NewBadge[]> {
    return this.http.put<NewBadgeList>(`${environment.apiUrl}/admin/menu-new-badges`, { badges }, ownErrorToastOptions())
      .pipe(
        map(MenuNewBadgeService.toList),
        tap(list => this.publish(list))
      );
  }

  private fetch(): Observable<NewBadge[]> {
    return this.http.get<NewBadgeList>(`${environment.apiUrl}/menu-new-badges`, ownErrorToastOptions())
      .pipe(map(MenuNewBadgeService.toList));
  }

  private publish(list: NewBadge[]): void {
    this.bootRequest?.unsubscribe();
    this.bootRequest = null;
    this.requested = true;
    this.badges$.next(list);
  }
}
